/**
 * SCROLL-BACK PAGING, the Slack way: a room opens on a screenful of the NEWEST messages at the bottom; scrolling up
 * toward the top quietly loads the next older page BEFORE you hit the edge (``prefetchPx`` early), and the view
 * does not jump — the message you were reading stays exactly where it was (we re-anchor by the height the older
 * page added). A short room that doesn't fill the screen keeps loading until it does (or history ends).
 *
 *   const { sentinelRef } = useScrollBack({ rootRef, hasMore, onLoadOlder, firstKey });
 *   <div ref={rootRef} className="overflow-y-auto"> <div ref={sentinelRef} /> …messages… </div>
 *
 * ``firstKey`` = the id of the oldest item shown (changes when a page is prepended → re-anchor + re-arm).
 * Paging only starts once the user has scrolled UP (or the content doesn't fill the view) — so opening a room,
 * which lands at the bottom, never cascades through the whole history.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

export default function useScrollBack({ rootRef, hasMore, onLoadOlder, firstKey = '', prefetchPx = 600 }) {
  const sentinelRef = useRef(null);
  const anchor = useRef(null);
  const busy = useRef(false);
  const armed = useRef(false);
  const lastTop = useRef(0);
  const cb = useRef(onLoadOlder);
  cb.current = onLoadOlder;

  const load = () => {
    const root = rootRef.current;
    if (!root || busy.current) return;
    busy.current = true;
    anchor.current = { h: root.scrollHeight, top: root.scrollTop };
    Promise.resolve()
      .then(() => cb.current())
      .catch(() => {})
      .finally(() => { busy.current = false; });
  };

  // arm on the first scroll UP (a room opens at the bottom; that initial landing must not page)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    lastTop.current = root.scrollTop;
    const onScroll = () => {
      if (root.scrollTop < lastTop.current) armed.current = true;
      lastTop.current = root.scrollTop;
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [rootRef]);

  // re-observe after every page (a sentinel still in view after a short page fires again at once)
  useEffect(() => {
    const root = rootRef.current;
    const el = sentinelRef.current;
    if (!hasMore || !root || !el) return undefined;
    if (root.scrollHeight <= root.clientHeight) { load(); return undefined; }   // fill the screen first
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (armed.current && entries.some((e) => e.isIntersecting)) load();
    }, { root, rootMargin: `${prefetchPx}px 0px 0px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, firstKey, rootRef, prefetchPx]); // eslint-disable-line react-hooks/exhaustive-deps

  // the older page rendered ABOVE → push the view down by exactly what it added (no jump)
  useLayoutEffect(() => {
    const root = rootRef.current;
    const a = anchor.current;
    if (!root || !a) return;
    anchor.current = null;
    root.scrollTop = a.top + (root.scrollHeight - a.h);
  }, [firstKey, rootRef]);

  return { sentinelRef, __armForTest: () => { armed.current = true; } };
}
